import React, { useEffect, useState, useContext } from "react";
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Drawer,
} from "@mui/material";
import {
  ArrowBack,
  Home,
  Settings,
  AccessAlarm,
  ShowChart,
  Menu,
} from "@mui/icons-material";
import GroupIcon from "@mui/icons-material/Group";
import LockIcon from "@mui/icons-material/Lock";
import SummarizeIcon from "@mui/icons-material/Summarize";
import "../../assets/css/menu.css";
import { useNavigate, useLocation } from "react-router-dom";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import AuthContext from "../../helpers/AuthContext";
import { useFeatures } from "../../helpers/FeatureContext";

const iconMap = {
  Home: <Home />,
  ShowChart: <ShowChart />,
  AccessAlarm: <AccessAlarm />,
  Settings: <Settings />,
  GroupIcon: <GroupIcon />,
  LockIcon: <LockIcon />,
  SummarizeIcon: <SummarizeIcon />,
  FingerprintIcon: <FingerprintIcon />,
};

// Function to build menu based on user role + per-tenant feature flags
const getMainMenu = (roleId, features = {}) => {
  // admins see everything; customers see only enabled features
  const gate = (flag) => (roleId === 1 ? false : !features[flag]);

  const settingsSubmenu = [
    { title: "Customers", icon: "GroupIcon", path: "/customers" },
    { title: "Origins", icon: "GroupIcon", path: "/origins" },
  ];

  // Only show Users for Admin (1) and Developer (2)
  if (roleId === 1 || roleId === 2) {
    settingsSubmenu.push({ title: "Users", icon: "GroupIcon", path: "/users" });
  }

  
  if (roleId === 1 || roleId === 2 || roleId === 3) {
    settingsSubmenu.push({
      title: "NLIS Settings",
      icon: "Settings",
      path: "/settings/nlis",
    });
  }

  return [
    {
      title: "Home",
      icon: "Home",
      path: "/",
    },
    {
      title: "Processing",
      icon: "AccessAlarm",
      submenu: [
        {
          title: "Scheduling",
          icon: "AccessAlarm",
          path: "/processing/scheduling",
          hidden: true,
        },
        {
          title: "Process Monitor",
          icon: "AccessAlarm",
          path: "/processing/process_monitor",
        },
      ],
    },
    {
      title: "Reporting",
      icon: "SummarizeIcon",
      path: "/reporting",
    },
    {
      title: "NLIS",
      icon: "FingerprintIcon",
      submenu: [
        {
          title: "ERP Enquiry",
          icon: "FingerprintIcon",
          path: "/nlis/erp-enquiry",
          hidden: gate("erp_enquiry"),
        },
        {
          title: "Device Status",
          icon: "FingerprintIcon",
          path: "/nlis/device-status",
          hidden: gate("device_status"),
        },
        {
          title: "View Holdings",
          icon: "FingerprintIcon",
          path: "/nlis/holdings",
          hidden: gate("view_holdings"),
        },
        {
          title: "ERP Exceptions",
          icon: "FingerprintIcon",
          path: "/nlis/erp-exceptions",
          hidden: gate("erp_exceptions"),
        },
        {
          title: "Kill Upload",
          icon: "FingerprintIcon",
          path: "/nlis/kill-register",
          hidden: gate("kill_uploads"),
        },
      ],
    },
    {
      title: "Settings",
      icon: "Settings",
      submenu: settingsSubmenu,
    },
  ];
};

const LeftNavbar = () => {
  const { user } = useContext(AuthContext);
  const { features } = useFeatures();
  const [menuStack, setMenuStack] = useState(() => {
    const savedStack = sessionStorage.getItem("menuStack");
    if (savedStack) {
      return JSON.parse(savedStack);
    }
    return [getMainMenu(user?.role_id, {})];
  });
  const [activeItem, setActiveItem] = useState(() => {
    return sessionStorage.getItem("activeItem") || "Home";
  });
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Rebuild menu when user role or feature flags change.
  // and company switch only.
  useEffect(() => {
    if (user?.role_id) {
      const newMenu = getMainMenu(user.role_id, features);
      setMenuStack([newMenu]);
      sessionStorage.setItem("menuStack", JSON.stringify([newMenu]));
    }
  }, [user?.role_id, features]);

  useEffect(() => {
    const savedPath = sessionStorage.getItem("currentPath");
    if (savedPath) {
      navigate(savedPath);
    } else {
      navigate("/");
    }
  }, [navigate]);

  // Update active item based on current location
  useEffect(() => {
    const path = location.pathname;

    if (path === "/" || path === "/home") {
      setActiveItem("Home");
    } else if (path.includes("/processing")) {
      setActiveItem(
        path.includes("scheduling") ? "Scheduling" : "Process Monitor"
      );
    } else if (path.includes("/reporting")) {
      setActiveItem("Reporting");
    } else if (path.includes("/nlis")) {
      if (path.includes("erp-enquiry")) setActiveItem("ERP Enquiry");
      else if (path.includes("device-status")) setActiveItem("Device Status");
      else if (path.includes("erp-exceptions")) setActiveItem("ERP Exceptions");
      else if (path.includes("holdings")) setActiveItem("View Holdings");
      else if (path.includes("kill-register")) setActiveItem("Kill Upload");
    } else if (path.includes("/customers")) {
      setActiveItem("Customers");
    } else if (path.includes("/origins")) {
      setActiveItem("Origins");
    } else if (path.includes("/users")) {
      setActiveItem("Users");
    }
  }, [location]);

  const handleMenuClick = (item) => {
    if (item.submenu) {
      // Filter out hidden items when navigating into a submenu
      const visibleItems = item.submenu.filter((i) => !i.hidden);
      if (visibleItems.length === 0) return; // nothing enabled — don't navigate
      const newStack = [...menuStack, visibleItems];
      setMenuStack(newStack);
      setActiveItem(visibleItems[0].title);
      navigate(visibleItems[0].path);

      sessionStorage.setItem("menuStack", JSON.stringify(newStack));
      sessionStorage.setItem("activeItem", visibleItems[0].title);
      sessionStorage.setItem("currentPath", visibleItems[0].path);
    } else {
      setActiveItem(item.title);

      sessionStorage.setItem("activeItem", item.title);
      if (item.path) {
        navigate(item.path);
        sessionStorage.setItem("currentPath", item.path);
      }
    }
  };

  const handleBackClick = () => {
    const newStack = menuStack.slice(0, -1);
    setMenuStack(newStack);

    if (newStack.length > 0) {
      const parentMenu = newStack[newStack.length - 1];
      if (parentMenu && parentMenu[0]) {
        setActiveItem(parentMenu[0].title);
        if (parentMenu[0].path) {
          navigate(parentMenu[0].path);

          sessionStorage.setItem("menuStack", JSON.stringify(newStack));
          sessionStorage.setItem("activeItem", parentMenu[0].title);
          sessionStorage.setItem("currentPath", parentMenu[0].path);
        }
      }
    }
  };

  const toggleNavbar = () => {
    setIsOpen(!isOpen);
  };

  const currentMenu = menuStack[menuStack.length - 1];

  return (
    <div className="d-flex flex-column">
      <div className={`sidebar ${isOpen ? "open" : "closed"}`}>
        <IconButton onClick={toggleNavbar} className="menu-toggle-button">
          <Menu />
        </IconButton>
        <Drawer
          variant="temporary"
          anchor="left"
          className="left-nav-fixed"
          open={isOpen}
          onClose={toggleNavbar}
          classes={{ paper: "left-nav-fixed" }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <List style={{ flex: 1 }}>
              {menuStack.length > 1 && (
                <ListItem
                  button
                  onClick={handleBackClick}
                  className="back-button"
                >
                  <ListItemIcon>
                    <ArrowBack style={{ color: "#000" }} />
                  </ListItemIcon>
                  <ListItemText primary="Back" style={{ color: "#000" }} />
                </ListItem>
              )}
              {currentMenu &&
                currentMenu
                  .filter((item) => !item.hidden)
                  .map((item, index) => (
                    <ListItem
                      button
                      key={index}
                      onClick={() => handleMenuClick(item)}
                      className={`${
                        activeItem === item.title ? " active-item" : "list-item"
                      } `}
                    >
                      <ListItemIcon>{iconMap[item.icon]}</ListItemIcon>
                      <ListItemText primary={item.title} />
                    </ListItem>
                  ))}
            </List>

            <div
              style={{
                padding: "16px",
                textAlign: "center",
                backgroundColor: "#f8f9fa",
              }}
            >
              <h4 className="text-acumen-color" style={{ margin: 0 }}>
                M E A T W O R X
              </h4>
            </div>
          </div>
        </Drawer>
      </div>
    </div>
  );
};

export default LeftNavbar;